<template>
    <div style="height:100%" :style="{borderRadius: origin === 'movement' ? '30px 30px 0px 0px' : ''}">
        <div id="editor-menu">
            <!-- >>> Segment 1: History -->
            <button type="button" @click="editor.chain().focus().undo().run()">
                <v-icon>
                    mdi-undo
                </v-icon>
            </button>
            <button type="button" @click="editor.chain().focus().redo().run()">
                <v-icon>
                    mdi-redo
                </v-icon>
            </button>
            <!-- <<< Segment 1: History -->
            <div class="divider" />
            <!-- >>> Segment 2: Text Format -->
            <button v-if="!disabledExtensions.includes('Bold')" type="button" @click="editor.chain().focus().toggleBold().run()">
                <v-icon>
                    mdi-format-bold
                </v-icon>
            </button>
            <button v-if="!disabledExtensions.includes('Underline')" type="button" @click="editor.chain().focus().toggleUnderline().run()">
                <v-icon>
                    mdi-format-underline
                </v-icon>
            </button>
            <button v-if="!disabledExtensions.includes('Heading1')" type="button" @click="editor.chain().focus().toggleHeading({ level: 3 }).run()">
                <v-icon>
                    mdi-size-xl
                </v-icon>
            </button>
            <button v-if="!disabledExtensions.includes('Heading2')" type="button" @click="editor.chain().focus().toggleHeading({ level: 2 }).run()">
                <v-icon>
                    mdi-size-xxl
                </v-icon>
            </button>
            <!-- <<< Segment 2: Text Format -->
            <div class="divider" />
            <!-- >>> Segment 3: Text Color -->
            <button v-if="!disabledExtensions.includes('Color')" type="button" @click="editor.chain().focus().unsetColor().run()">
              <v-icon color="black">
                  mdi-square
              </v-icon>
            </button>
            <button v-if="!disabledExtensions.includes('Color')" type="button" @click="editor.chain().focus().setColor('red').run()">
              <v-icon color="red">
                  mdi-square
              </v-icon>
            </button>
            <!-- <<< Segment 3: Text Color -->
            <div class="divider" />
            <!-- >>> Segment 4: List -->
            <button v-if="!disabledExtensions.includes('BulletList')" type="button" @click="editor.chain().focus().toggleBulletList().run()">
                <v-icon>
                    mdi-format-list-bulleted
                </v-icon>
            </button>
            <!-- <<< Segment 4: List -->
            <div class="divider" />
            <!-- >>> Segment 5: Blockquote -->
            <button v-if="!disabledExtensions.includes('Blockquote')" type="button" @click="editor.chain().focus().toggleBlockquote().run()">
                <v-icon>
                    mdi-format-quote-close
                </v-icon>
            </button>
            <!-- <<< Segment 5: Blockquote -->
            <div class="divider" />
            <!-- >>> Segment 6: Table -->
            <button v-if="!disabledExtensions.includes('Table')" type="button" :disabled="editor && editor.isActive('table')" :class="editor && editor.isActive('table') ? 'button-disabled' : ''" @click="editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()">
                <v-icon>
                    mdi-table
                </v-icon>
            </button>
            <v-tooltip v-if="editor && editor.isActive('table')" bottom>
                <template v-slot:activator="{ on }">
                    <button type="button" class="tint" v-on="on" @click="editor.chain().focus().addColumnBefore().run()">
                        <v-icon>
                            mdi-table-column-plus-before
                        </v-icon>
                    </button>
                </template>
                <span>Add column before</span>
            </v-tooltip>
            <v-tooltip v-if="editor && editor.isActive('table')" bottom>
                <template v-slot:activator="{ on }">
                    <button v-if="editor && editor.isActive('table')" type="button" class="tint" v-on="on" @click="editor.chain().focus().addColumnAfter().run()">
                        <v-icon>
                            mdi-table-column-plus-after
                        </v-icon>
                    </button>
                </template>
                <span>Add column after</span>
            </v-tooltip>
            <v-tooltip v-if="editor && editor.isActive('table')" bottom>
                <template v-slot:activator="{ on }">
                    <button v-if="editor && editor.isActive('table')" type="button" class="tint" v-on="on" @click="editor.chain().focus().deleteColumn().run()">
                        <v-icon>
                            mdi-table-column-remove
                        </v-icon>
                    </button>
                </template>
                <span>Remove column</span>
            </v-tooltip>
            <v-tooltip v-if="editor && editor.isActive('table')" bottom>
                <template v-slot:activator="{ on }">
                    <button v-if="editor && editor.isActive('table')" type="button" class="tint" v-on="on" @click="editor.chain().focus().addRowAfter().run()">
                        <v-icon>
                            mdi-table-row-plus-after
                        </v-icon>
                    </button>
                </template>
                <span>Add row after</span>
            </v-tooltip>
            <v-tooltip v-if="editor && editor.isActive('table')" bottom>
                <template v-slot:activator="{ on }">
                    <button v-if="editor && editor.isActive('table')" type="button" class="tint" v-on="on" @click="editor.chain().focus().deleteRow().run()">
                        <v-icon>
                            mdi-table-row-remove
                        </v-icon>
                    </button>
                </template>
                <span>Remove row</span>
            </v-tooltip>
            <!-- <<< Segment 6: Table -->
        </div>
        <editor-content id="editor-content" :editor="editor" style="height:calc(100% - 50px)" />
    </div>
</template>

<script>
    import { Editor, EditorContent } from '@tiptap/vue-2';
    import StarterKit from '@tiptap/starter-kit';
    import Placeholder from '@tiptap/extension-placeholder';

    export default {
        /* >>> *************************************************************** / /* Components */
        components: {

            EditorContent

        },
        /* <<< *************************************************************** / /* Props */

        /* >>> *************************************************************** / /* Props */
        props: {

            value: {
                type: String,
                default: ''
            },
            disabledExtensions: {
                type: Array,
                default: null
            },
            origin: {
                type: String,
                default: ''
            }

        },
        /* <<< *************************************************************** / /* Props */

        /* >>> *************************************************************** / /* Data */
        data() {

            return {
                editor: null
            };

        }, // eo funciton beforeDestroy()
        /* <<< *************************************************************** / /* LifeCycles */

        /* >>> *************************************************************** / /* Watch */
        watch: {

            /*******************************************************************
			* value()
			*
			* Desc: Set the new content, if something changed.
			* Note: Do nothing if the content hasn't changed.
			*
			*/
            value(value) {

                const contentIsSame = this.editor.getHTML() === value;


                if (contentIsSame) {
                    return;
                }

                this.editor.commands.setContent(value, false);

            } // eo function value()

        },
        /* <<< *************************************************************** / /* Data */


        /* >>> *************************************************************** /  /* LifeCycles */
        /***********************************************************************
        * mounted
        *
        * Desc:
        * Note:
        *
        */
        mounted() {

            this.editor = new Editor({
                content: this.value,
                extensions: [
                    StarterKit,
                    Placeholder.configure({
                        placeholder: 'Paste your message here..'
                    })
                ],
                onUpdate: () => {
                    this.$emit('input', this.editor.getHTML());
                }
            });

        }, // eo function mounted()


        /***********************************************************************
        * beforeDestroy
        *
        * Desc:
        * Note:
        *
        */
        beforeDestroy() {

            this.editor.destroy();

        }
        /* <<< *************************************************************** / /* Watch */

    };
</script>

<style lang="scss">
    .ProseMirror p.is-editor-empty:first-child::before {
        content: attr(data-placeholder);
        float: left;
        color: #adb5bd;
        pointer-events: none;
        height: 0;
    }
    #editor-menu {
        position: sticky;
        top: 0;
        z-index: 2;
        padding: 8px 12px;
        background: #DDD;
        border-bottom: 1px solid #666;

        button {
            background-color: #FFF;
            padding: 4px;
            border-radius: 4px;
            margin: 0 3px;

            &.tint {
                background: #FFF;
                padding: 0 3px;

                .v-icon {
                    font-size: 17px;
                }
            }

            &.button-disabled {
                background: #EEE;
            }
        }

         .divider {
            margin: 0 10px;
            display: inline;
        }
    }

    #editor-content{
        padding: 10px;

        p {
            margin: 0 !important;
        }

        .ProseMirror:focus {
            outline:0;
        }

        .ProseMirror {
            height: 100%;
        }

        blockquote {
            font-style: italic;
        }

        table {
            border-collapse: collapse;
            table-layout: fixed;
            width: 100%;
            margin: 0;
            overflow: hidden;

            td,
            th {
                min-width: 1em;
                border: 2px solid #ced4da;
                padding: 3px 5px;
                vertical-align: top;
                box-sizing: border-box;
                position: relative;

                > * {
                    margin-bottom: 0;
                }
            }

            th {
                font-weight: bold;
                text-align: left;
                background-color: #f1f3f5;
            }

            .selectedCell:after {
                z-index: 2;
                position: absolute;
                content: "";
                left: 0; right: 0; top: 0; bottom: 0;
                background: rgba(200, 200, 255, 0.4);
                pointer-events: none;
            }

            .column-resize-handle {
                position: absolute;
                right: -2px;
                top: 0;
                bottom: -2px;
                width: 4px;
                background-color: #adf;
                pointer-events: none;
            }

            p {
                margin: 0 !important;
            }
        }
    }
</style>
